import { useState } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

export function XeroWouldQuarantinePanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const { data: result } = await diagnosticsAPI.xeroWouldQuarantine();
      setData(result);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const verdictBadge = (verdict) => {
    if (verdict === 'clean') {
      return (
        <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
          <CheckCircle2 className="h-3 w-3 mr-1" /> CLEAN
        </Badge>
      );
    }
    return (
      <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
        <AlertTriangle className="h-3 w-3 mr-1" /> ACTION NEEDED
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Xero Would-Quarantine Audit</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only scan of already-synced Xero invoices. Reports records with
          null/missing amounts or non-numeric values that would be quarantined
          on re-sync. Does not mutate data.
        </p>
        <Button onClick={run} disabled={loading} size="sm">
          {loading
            ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
            : <Search className="h-4 w-4 mr-2" />
          }
          Scan
        </Button>

        {data && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {verdictBadge(data.verdict)}
              <span className="text-xs text-muted-foreground">
                {fmtInt(data.total_xero_invoices)} total Xero invoices
              </span>
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Metric</th>
                    <th className="text-right px-2 py-1.5 font-medium">Count</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="px-2 py-1">Already quarantined (guards caught)</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtInt(data.already_quarantined)}</td>
                  </tr>
                  <tr className={`border-b ${data.would_quarantine?.null_amount > 0 ? 'bg-red-50 dark:bg-red-950' : ''}`}>
                    <td className="px-2 py-1">Null/missing amount (would quarantine)</td>
                    <td className="px-2 py-1 text-right tabular-nums font-medium">{fmtInt(data.would_quarantine?.null_amount)}</td>
                  </tr>
                  <tr className={`border-b ${data.would_quarantine?.non_numeric_amount > 0 ? 'bg-red-50 dark:bg-red-950' : ''}`}>
                    <td className="px-2 py-1">Non-numeric amount (would quarantine)</td>
                    <td className="px-2 py-1 text-right tabular-nums font-medium">{fmtInt(data.would_quarantine?.non_numeric_amount)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-2 py-1">Missing currency</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtInt(data.data_quality?.missing_currency)}</td>
                  </tr>
                  <tr className="border-b last:border-0">
                    <td className="px-2 py-1">Missing FX rate (non-GBP)</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtInt(data.data_quality?.missing_fx_rate_non_gbp)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {data.examples && data.examples.length > 0 && (
              <div>
                <p className="text-xs font-medium mb-1">Examples (up to 10 per category):</p>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Category</th>
                        <th className="text-left px-2 py-1.5 font-medium">Invoice #</th>
                        <th className="text-left px-2 py-1.5 font-medium">Entity</th>
                        <th className="text-right px-2 py-1.5 font-medium">Amount</th>
                        <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.examples.map((ex, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono text-[10px]">{ex.category}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{ex.invoice_number || '-'}</td>
                          <td className="px-2 py-1 font-mono text-[10px]">{ex.entity_name || '-'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{ex.amount != null ? String(ex.amount) : 'null'}</td>
                          <td className="px-2 py-1">{ex.currency || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
